import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import styles from './styles';

interface Column {
  value?: string | number;
  bold?: boolean;
  italic?: boolean;
  width?: number;
}

interface TableRowProps {
  data: Record<string, Column>;
  backgroundColor?: string;
  fontColor?: string;
  isFirstRow?: boolean;
  alignContent?: 'left' | 'center' | 'right';
  fontSize?: number;
  fullBorder?: boolean;
  isFooter?: boolean;
  expandRows?: boolean;
  tallRows?: boolean;
}

const TableRow: React.FC<TableRowProps> = ({
  data,
  backgroundColor,
  fontColor,
  isFirstRow,
  alignContent,
  fontSize,
  fullBorder,
  isFooter,
  expandRows,
  tallRows,
}) => {
  const itemsCount = Object.keys(data).length;

  const fixedWidthSum = Object.values(data).reduce((width, column) => {
    return width + (column.width ?? 0);
  }, 0);
  const itemsWithFixedWidth = Object.values(data).filter(
    (column) => !!column.width,
  ).length;

  const autoWidth =
    itemsCount > itemsWithFixedWidth
      ? (100 - fixedWidthSum) / (itemsCount - itemsWithFixedWidth)
      : 0;

  // check if any of the data.property values are greater than 40
  const rowContainsLongText = Object.values(data).some(
    (column) => (column.value?.toString().length ?? 0) > 40,
  );

  return (
    <View
      style={[
        styles.tableRow,
        expandRows ? styles.expandingRow : styles.fixedRow,
        fullBorder ? styles.fullBorderRow : styles.partialBorderRow,
        (tallRows || rowContainsLongText) && !expandRows ? styles.tallRow : {},
        {
          backgroundColor,
          borderTopWidth: isFirstRow ? 1 : 0.5,
          borderBottomWidth: isFooter ? 0 : 0.5,
        },
      ]}
      wrap={true}
    >
      {Object.entries(data).map(([id, { value, bold, italic, width }]) => (
        <TableColumn
          key={id}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
          value={value}
          width={`${width ?? autoWidth}%`}
          bold={bold}
          italic={italic}
          alignContent={alignContent}
          fontSize={fontSize}
          fullBorder={fullBorder}
          isFooter={isFooter}
          tallRows={tallRows}
        />
      ))}
    </View>
  );
};

interface TableColumnProps {
  value?: Column['value'];
  backgroundColor?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  width: string;
  alignContent?: 'left' | 'center' | 'right';
  fontSize?: number;
  fullBorder?: boolean;
  isFooter?: boolean;
  tallRows?: boolean;
  expandRows?: boolean;
}

const TableColumn: React.FC<TableColumnProps> = ({
  value,
  backgroundColor,
  fontColor,
  bold,
  italic,
  width,
  alignContent,
  fontSize,
  fullBorder,
  isFooter,
  tallRows,
  expandRows,
}) => {
  const baseStyles = [
    styles.tableCol,
    fullBorder && (!isFooter || value) ? styles.fullBorderCol : {},
    alignContent === 'center' ? styles.alignCenter : styles.alignLeft,
    tallRows && !expandRows ? styles.tallRowText : {},
  ];

  const dynamicStyles = {
    backgroundColor,
    color: fontColor,
    width,
    maxWidth: width,
    fontSize,
    borderBottomWidth: fullBorder && isFooter && value ? 1 : 0,
  };

  const fontStyles = bold ? styles.bold : italic ? styles.italic : styles.text;

  const textStyle = [...baseStyles, dynamicStyles, fontStyles];

  return (
    <View wrap={false} style={textStyle}>
      {value?.toString().length ? (
        <Text
          style={
            alignContent === 'center'
              ? styles.tableCellValueCenter
              : styles.tableCellValueLeft
          }
          key={value.toString()}
        >
          {value.toString()}
        </Text>
      ) : null}
    </View>
  );
};

interface TableTitleProps {
  title: string;
  backgroundColor?: string;
  fontColor?: string;
}

const TableTitle: React.FC<TableTitleProps> = ({
  title,
  backgroundColor,
  fontColor,
}) => (
  <View
    style={[
      styles.tableRow,
      styles.tableTitleContainer,
      { backgroundColor, borderWidth: 1 },
    ]}
  >
    <Text style={[styles.tableTitle, styles.bold, { color: fontColor }]}>
      {title}
    </Text>
  </View>
);

const TableContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <View style={styles.table} wrap={true}>
    {children}
  </View>
);

interface TableProps {
  title?: string | null;
  columns?: Record<string, Column>;
  backgroundColor?: string;
  fontColor?: string;
  headerFontColor?: string;
  data: Array<Record<string, Column>>;
  alignContent?: 'left' | 'center' | 'right';
  footer?: Record<string, Column> | null;
  fontSize?: number;
  fullBorder?: boolean;
  expandRows?: boolean; // removes any height from the row which allows the row to expand to fit the content
  tallRows?: boolean; // adds a fixed height to the row, doesn't work if expandRows is true
}

const Table: React.FC<TableProps> = ({
  title,
  columns,
  backgroundColor,
  fontColor,
  headerFontColor = fontColor,
  data,
  alignContent = 'center',
  footer,
  fontSize = 7,
  fullBorder = false,
  expandRows = false,
  tallRows = false,
}) => (
  <TableContainer>
    {title ? (
      <TableTitle
        title={title}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
      />
    ) : null}
    {columns ? (
      <TableRow
        data={columns}
        backgroundColor={backgroundColor}
        fontColor={headerFontColor ?? fontColor}
        isFirstRow={!title}
        alignContent={alignContent}
        fontSize={fontSize}
        fullBorder={fullBorder}
        expandRows={expandRows}
      />
    ) : null}
    {data.length
      ? data.map((rowData, rowId) => (
          <TableRow
            // eslint-disable-next-line react/no-array-index-key
            key={rowId}
            data={rowData}
            isFirstRow={false}
            alignContent={alignContent}
            fontSize={fontSize}
            fullBorder={fullBorder}
            expandRows={expandRows}
            tallRows={tallRows}
          />
        ))
      : null}
    {footer ? (
      <TableRow
        data={footer}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
        isFirstRow={false}
        alignContent={alignContent}
        fontSize={fontSize}
        fullBorder={fullBorder}
        expandRows={expandRows}
        isFooter
      />
    ) : null}
  </TableContainer>
);

export default Table;
